import { ref } from 'vue'
import type { Ref } from 'vue'
import {
  listEventMedia,
  getEventMedia,
  uploadEventHero,
  deleteEventHero,
} from '@/api/eventMedia'
import type { EventMedia } from '@/types/eventMedia'
import { useEvents } from '@/composables/useEvents'

export function useEventMedia(eventSlug: string): {
  hero: Ref<EventMedia | null>
  loading: Ref<boolean>
  error: Ref<string | null>
  fieldErrors: Ref<Record<string, string[]>>
  uploadProgress: Ref<number>
  fetchHero: () => Promise<void>
  uploadHero: (file: File, onProgress?: (pct: number) => void) => Promise<EventMedia>
  deleteHero: () => Promise<void>
  startPolling: (mediaId: number) => void
  stopPolling: () => void
} {
  const { lastSavedAt } = useEvents()

  const hero = ref<EventMedia | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const fieldErrors = ref<Record<string, string[]>>({})
  const uploadProgress = ref(0)
  const pollHandle = ref<ReturnType<typeof setInterval> | null>(null)

  async function fetchHero(): Promise<void> {
    loading.value = true
    error.value = null
    try {
      const results = await listEventMedia(eventSlug, 'hero')
      hero.value = results[0] ?? null

      if (
        hero.value &&
        (hero.value.status === 'pending' || hero.value.status === 'processing')
      ) {
        startPolling(hero.value.id)
      }
    } catch {
      error.value = 'common.error_generic'
    } finally {
      loading.value = false
    }
  }

  async function uploadHero(
    file: File,
    onProgress?: (pct: number) => void,
  ): Promise<EventMedia> {
    fieldErrors.value = {}
    uploadProgress.value = 0

    const uploaded = await uploadEventHero(eventSlug, file, (pct) => {
      uploadProgress.value = pct
      onProgress?.(pct)
    })

    hero.value = uploaded
    lastSavedAt.value = new Date()

    if (uploaded.status === 'pending' || uploaded.status === 'processing') {
      startPolling(uploaded.id)
    }

    return uploaded
  }

  async function deleteHero(): Promise<void> {
    await deleteEventHero(eventSlug)
    hero.value = null
    lastSavedAt.value = new Date()
  }

  function startPolling(mediaId: number): void {
    stopPolling()

    pollHandle.value = setInterval(async () => {
      try {
        const media = await getEventMedia(eventSlug, mediaId)
        hero.value = media

        if (media.status === 'ready' || media.status === 'failed') {
          stopPolling()

          if (media.status === 'ready') {
            lastSavedAt.value = new Date()
          }
        }
      } catch {
        stopPolling()
      }
    }, 3000)
  }

  function stopPolling(): void {
    if (pollHandle.value !== null) {
      clearInterval(pollHandle.value)
      pollHandle.value = null
    }
  }

  function extractFieldErrors(e: unknown): Record<string, string[]> {
    if (
      typeof e === 'object' &&
      e !== null &&
      'response' in e &&
      typeof (e as { response: unknown }).response === 'object' &&
      (e as { response: { data?: unknown } }).response !== null
    ) {
      const response = (e as { response: { data?: unknown } }).response
      if (
        typeof response.data === 'object' &&
        response.data !== null &&
        'errors' in response.data
      ) {
        const errors = (response.data as { errors: unknown }).errors
        if (typeof errors === 'object' && errors !== null) {
          return errors as Record<string, string[]>
        }
      }
    }
    return {}
  }

  return {
    hero,
    loading,
    error,
    fieldErrors,
    uploadProgress,
    fetchHero,
    uploadHero: async (file: File, onProgress?: (pct: number) => void) => {
      try {
        return await uploadHero(file, onProgress)
      } catch (e: unknown) {
        const errors = extractFieldErrors(e)
        if (Object.keys(errors).length > 0) {
          fieldErrors.value = errors
        } else {
          fieldErrors.value = {}
        }
        throw e
      }
    },
    deleteHero,
    startPolling,
    stopPolling,
  }
}
